import { useState, useMemo } from 'react';
import { useAuditLogs, type AuditLog } from '../hooks/useAuditLogs';
import { Select } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { Shield, Filter, Calendar, User, FileText, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

// Mapeamento de entidades para nomes amigáveis
const ENTITY_LABELS: Record<string, string> = {
  TimeEntry: 'Registro de Ponto',
  Timesheet: 'Folha de Ponto',
  TimeAdjustmentRequest: 'Ajuste de Ponto',
  Project: 'Projeto',
  Activity: 'Atividade',
  TimeAllocation: 'Lançamento de Horas',
  EmployeeProjectAllocation: 'Alocação em Projeto',
  Employee: 'Colaborador',
  OperationalCost: 'Despesa',
};

// Mapeamento de ações para nomes amigáveis e cores
const ACTION_CONFIG: Record<string, { label: string; variant: 'default' | 'success' | 'warning' | 'error' | 'info' }> = {
  CREATE: { label: 'Criação', variant: 'success' },
  UPDATE: { label: 'Atualização', variant: 'info' },
  DELETE: { label: 'Exclusão', variant: 'error' },
  APPROVE: { label: 'Aprovação', variant: 'success' },
  REJECT: { label: 'Rejeição', variant: 'error' },
  CLOCK_IN: { label: 'Entrada', variant: 'info' },
  CLOCK_OUT: { label: 'Saída', variant: 'info' },
  BREAK_START: { label: 'Saída Almoço', variant: 'warning' },
  BREAK_END: { label: 'Volta Almoço', variant: 'warning' },
  ALLOCATE: { label: 'Alocação', variant: 'success' },
  DEALLOCATE: { label: 'Desalocação', variant: 'warning' },
  RECALCULATE: { label: 'Recálculo', variant: 'default' },
};

function AuditLogRow({ log, onClick }: { log: AuditLog; onClick: () => void }) {
  const actionConfig = ACTION_CONFIG[log.action] || { label: log.action, variant: 'default' };
  const entityLabel = ENTITY_LABELS[log.entity] || log.entity;

  return (
    <tr
      onClick={onClick}
      className="hover:bg-accent cursor-pointer transition-colors border-b border-border"
    >
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {format(new Date(log.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
      </td>
      <td className="px-4 py-3">
        <Badge variant={actionConfig.variant} className="text-xs">
          {actionConfig.label}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm font-medium text-foreground">
        {entityLabel}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {log.user?.name || 'Sistema'}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground">
        {log.user?.role || '-'}
      </td>
    </tr>
  );
}

function AuditLogDetailModal({ log, onClose }: { log: AuditLog | null; onClose: () => void }) {
  if (!log) return null;

  const actionConfig = ACTION_CONFIG[log.action] || { label: log.action, variant: 'default' };
  const entityLabel = ENTITY_LABELS[log.entity] || log.entity;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-card rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Shield className="h-5 w-5" />
              Detalhes do Log de Auditoria
            </h2>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Info básica */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Data/Hora</p>
              <p className="text-sm text-foreground mt-1">
                {format(new Date(log.createdAt), "dd/MM/yyyy 'às' HH:mm:ss", { locale: ptBR })}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Ação</p>
              <div className="mt-1">
                <Badge variant={actionConfig.variant}>{actionConfig.label}</Badge>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Entidade</p>
              <p className="text-sm text-foreground mt-1">{entityLabel}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">ID da Entidade</p>
              <p className="text-sm text-muted-foreground mt-1 font-mono text-xs">
                {log.entityId}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Usuário</p>
              <p className="text-sm text-foreground mt-1">
                {log.user?.name || 'Sistema'}
                {log.user?.email && (
                  <span className="text-xs text-muted-foreground block">{log.user.email}</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">Papel</p>
              <p className="text-sm text-foreground mt-1">{log.user?.role || '-'}</p>
            </div>
          </div>

          {/* Before/After */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {log.before && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Estado Anterior</p>
                <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-64">
                  {JSON.stringify(log.before, null, 2)}
                </pre>
              </div>
            )}
            {log.after && (
              <div>
                <p className="text-sm font-medium text-muted-foreground mb-2">Estado Novo</p>
                <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-64">
                  {JSON.stringify(log.after, null, 2)}
                </pre>
              </div>
            )}
          </div>

          {/* Metadata */}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-2">Metadados</p>
              <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-32">
                {JSON.stringify(log.metadata, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AuditLogsPage() {
  const [entityFilter, setEntityFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('24h');
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);

  // Calcular datas baseado no período
  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    const end = now.toISOString();
    let start = '';

    if (periodFilter === '24h') {
      start = new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    } else if (periodFilter === '7d') {
      start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    } else if (periodFilter === '30d') {
      start = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    }

    return { startDate: start, endDate: end };
  }, [periodFilter]);

  const { data, isLoading } = useAuditLogs({
    entity: entityFilter || undefined,
    action: actionFilter || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    limit: 100,
  });

  const logs: AuditLog[] = data?.data || [];
  const total = data?.meta?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
          <Shield className="h-8 w-8" />
          Logs de Auditoria
        </h1>
        <p className="text-muted-foreground mt-1">
          Rastreie todas as alterações críticas no sistema PeopleCore
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <FileText className="h-4 w-4" />
            <span className="text-sm font-medium">Total de Logs</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{total}</p>
        </div>
        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Clock className="h-4 w-4" />
            <span className="text-sm font-medium">Período</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {periodFilter === '24h' ? '24h' : periodFilter === '7d' ? '7 dias' : '30 dias'}
          </p>
        </div>
        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <User className="h-4 w-4" />
            <span className="text-sm font-medium">Filtros Ativos</span>
          </div>
          <p className="text-2xl font-bold text-foreground">
            {[entityFilter, actionFilter].filter(Boolean).length}
          </p>
        </div>
        <div className="bg-card p-4 rounded-lg border border-border">
          <div className="flex items-center gap-2 text-muted-foreground mb-1">
            <Filter className="h-4 w-4" />
            <span className="text-sm font-medium">Exibindo</span>
          </div>
          <p className="text-2xl font-bold text-foreground">{logs.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card p-4 rounded-lg border border-border">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold text-foreground">Filtros</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Período */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Período
            </label>
            <Select
              value={periodFilter}
              onChange={(e) => setPeriodFilter(e.target.value)}
              options={[
                { value: '24h', label: 'Últimas 24 horas' },
                { value: '7d', label: 'Últimos 7 dias' },
                { value: '30d', label: 'Últimos 30 dias' },
              ]}
            />
          </div>

          {/* Entidade */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Entidade
            </label>
            <Select
              value={entityFilter}
              onChange={(e) => setEntityFilter(e.target.value)}
              options={[
                { value: '', label: 'Todas' },
                { value: 'TimeEntry', label: 'Registro de Ponto' },
                { value: 'Timesheet', label: 'Folha de Ponto' },
                { value: 'TimeAdjustmentRequest', label: 'Ajuste de Ponto' },
                { value: 'Project', label: 'Projeto' },
                { value: 'Activity', label: 'Atividade' },
                { value: 'TimeAllocation', label: 'Lançamento de Horas' },
                { value: 'EmployeeProjectAllocation', label: 'Alocação em Projeto' },
                { value: 'OperationalCost', label: 'Despesa' },
              ]}
            />
          </div>

          {/* Ação */}
          <div>
            <label className="text-sm font-medium text-muted-foreground mb-2 flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Ação
            </label>
            <Select
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
              options={[
                { value: '', label: 'Todas' },
                { value: 'CREATE', label: 'Criação' },
                { value: 'UPDATE', label: 'Atualização' },
                { value: 'DELETE', label: 'Exclusão' },
                { value: 'APPROVE', label: 'Aprovação' },
                { value: 'REJECT', label: 'Rejeição' },
                { value: 'CLOCK_IN', label: 'Entrada' },
                { value: 'CLOCK_OUT', label: 'Saída' },
                { value: 'BREAK_START', label: 'Saída Almoço' },
                { value: 'BREAK_END', label: 'Volta Almoço' },
                { value: 'ALLOCATE', label: 'Alocação' },
                { value: 'DEALLOCATE', label: 'Desalocação' },
                { value: 'RECALCULATE', label: 'Recálculo' },
              ]}
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Data/Hora
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Ação
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Entidade
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Usuário
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">
                  Papel
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8">
                    <div className="flex items-center justify-center">
                      <Spinner />
                    </div>
                  </td>
                </tr>
              ) : logs.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-muted-foreground">
                    Nenhum log encontrado para os filtros selecionados
                  </td>
                </tr>
              ) : (
                logs.map((log) => (
                  <AuditLogRow key={log.id} log={log} onClick={() => setSelectedLog(log)} />
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail Modal */}
      <AuditLogDetailModal log={selectedLog} onClose={() => setSelectedLog(null)} />
    </div>
  );
}
